import { TranslatedText } from "@/components/common/text/TranslatedText";
import { HomepageMap } from "@/components/home/HomepageMap";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { clearSearchState } from "@/state/searchSlice";
import { clearInputsExceptFixedDropdowns } from "@/state/searchInputSlice";
import { API } from "@/services/API";
import { MarketCard } from "@/components/home/MarketCard";
import { InfoButton } from "@/components/common/buttons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, A11y, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { MapLegend } from "@/components/maps/MapLegend";

export const Homepage = () => {
    const dispatch = useDispatch();
    const [hasError, setHasError] = useState(false);
    const [outboundPerMarket, setOutboundPerMarket] = useState({});
    const [markets, setMarkets] = useState([]);
    const [growers, setGrowers] = useState([]);
    const [decliners, setDecliners] = useState([]);
    const [sliderIndex, setSliderIndex] = useState(0);

    useEffect(() => {
        dispatch(clearSearchState());
        dispatch(clearInputsExceptFixedDropdowns());
    }, []);

    useEffect(() => {
        API.get("/analytics/outbound-rpm-per-market?trailer=dry_van&interval-in-days=30")
            .then((response) => {
                const temp = {};
                response.forEach((v) => {
                    temp[v.averageStat.label.split(",")[0]] = v;
                });
                setOutboundPerMarket(temp);
                setMarkets(response);
            })
            .catch(() => {
                setHasError(true);
            });
    }, []);

    useEffect(() => {
        const sortedMarkets = markets
            .filter((t) => t.confidence === "high" || t.confidence === "medium")
            .sort((a, b) => (a.trend < b.trend ? -1 : 1));
        setDecliners([...sortedMarkets].slice(0, 5));
        setGrowers([...sortedMarkets].slice(sortedMarkets.length - 5, sortedMarkets.length).reverse());
    }, [markets]);

    return (
        <div className="text-cm-text flex flex-col items-center justify-start md:justify-center w-full grow min-h-full">
            <div className="w-full lg:w-[1000px] overflow-hidden relative min-h-[50.78125vw] xl:min-h-[500px]">
                <HomepageMap hasError={hasError} setHasError={setHasError} outboundPerMarket={outboundPerMarket} />
                <MapLegend />
            </div>
            {!hasError ? (
                <div className="w-full mt-[55px] mb-[24px]">
                    <div className="w-full text-center flex flex-row items-center justify-center mb-3 text-cm-p-text-primary text-base leading-[25px] font-medium gap-[10px]">
                        <TranslatedText>{`${sliderIndex ? "Falling" : "Rising"} markets`}</TranslatedText>
                        <InfoButton text="Markets with the highest and lowest changes in average outbound RPM, comparing today’s prices to those from 30 days ago." />
                    </div>
                    <div className="mx-4 lg:mx-0">
                        <Swiper
                            loop
                            slidesPerView={1}
                            pagination={{ clickable: true }}
                            onSlideChange={(e) => {
                                setSliderIndex(e.realIndex);
                            }}
                            modules={[Autoplay, Pagination, A11y, Navigation]}
                            navigation
                            autoplay={{
                                delay: 10000,
                                disableOnInteraction: false,
                            }}
                        >
                            <SwiperSlide>
                                <div className="flex flex-col lg:flex-row items-center justify-center mt-1 mb-4 gap-4">
                                    {growers.map((g, i) => (
                                        <MarketCard key={i} trend={g.trend} avgRpm={g.averageStat.average} name={g.averageStat.label} />
                                    ))}
                                </div>
                            </SwiperSlide>
                            <SwiperSlide>
                                <div className="flex flex-col lg:flex-row items-center justify-center mt-1 mb-8 gap-4">
                                    {decliners.map((d, i) => (
                                        <MarketCard key={i} trend={d.trend} avgRpm={d.averageStat.average} name={d.averageStat.label} />
                                    ))}
                                </div>
                            </SwiperSlide>
                        </Swiper>
                    </div>
                    <div className="w-full text-cm-p-text-secondary text-sm leading-[22px] text-center mt-[28px] mx-4">
                        <TranslatedText>
                            The values shown on the map and the trends are based on the last 30 days for dry van.
                        </TranslatedText>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
